import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { Subscription } from "../../bt-common/schemas/bt/subscription";
import { safeDispatch as dispatch, getStateDirectly } from '@common/state/state-provider';
import { UpdateEvent } from "@common/types/interface";
import { setLoaderProgressMessage } from "../reducers/ChatbotReducer";
import { DBSubscriptionThunks, TeamInboxChatsCacheReducerActions, TeamInboxMessagesCacheReducerActions } from "../reducers/DBServiceThunks";
import { updateDocumentProgress } from "../reducers/KnowledgeBaseProgressSlice";
import { setSettings } from "../reducers/MainReducer";
import { requestChatByIdThunk, requestSendSeenByChatIdThunk } from "../../components/team-inbox/redux/TeamInboxThunks";
import { validateSubscriptionsThunk } from "../reducers/UserThunks";
import { isExtensionContext } from "../services/detect-context";
import { CommonRootState } from "../types/common-root-state-type";
import { MQTTSubscriber } from "./MQTTSubscriber";
import { processMessage } from "./NotiferHandlers";
import { handleWhatsappInterfaceEvents } from "@common/tools/whatsapp";
import { setConnectionStatus } from "./NotifierReducer";
import { NotifierConnectionStatus, UpdateEventMessageData } from "./types";
import { isSystemNotificationMessage } from "../../components/team-inbox/team-inbox-helpers";


/** The cached chat's last-message key: a query-loaded chat carries the message
 *  ROW (`waMessageKey`), a live listener message carries `id` (QA 3b E5). */
const lastMessageKeyOf = (chat: any): string | undefined =>
  chat?.lastMessage?.waMessageKey?._serialized ?? chat?.lastMessage?.id?._serialized;

export class NotifierService {
  private static instance: NotifierService;
  private mqttSubscriber: MQTTSubscriber;
  private userId: string | null = null;
  private accessToken: string | null = null;

  private constructor() {
    this.mqttSubscriber = MQTTSubscriber.getInstance();
  }

  public static getInstance(): NotifierService {
    if (!NotifierService.instance) {
      NotifierService.instance = new NotifierService();
    }
    return NotifierService.instance;
  }

  public disconnect(): void {
    console.log('[Notifier] Disconnecting MQTT');
    this.mqttSubscriber.disconnect();
    this.userId = null;
    this.accessToken = null;
    dispatch(setConnectionStatus(NotifierConnectionStatus.DISCONNECTED));
  }

  public connect(accessToken: string): void {
    // Check if already connected with the same token
    if (this.mqttSubscriber.isConnected() && this.accessToken === accessToken) {
      console.log('[Notifier] MQTT already connected with same token');
      return;
    }

    console.log('[Notifier] Connecting via MQTT');
    this.accessToken = accessToken;

    // Extract user ID from JWT token
    this.userId = this.extractUserIdFromToken(accessToken);

    if (!this.userId) {
      console.error('[Notifier] Cannot extract user ID from access token');
      dispatch(setConnectionStatus(NotifierConnectionStatus.UNAUTHENTICATED));
      return;
    }

    dispatch(setConnectionStatus(NotifierConnectionStatus.CONNECTED));

    // Setup MQTT message handlers - the MQTTSubscriber will handle deduplication
    this.setupMQTTHandlers();

    // Connect to MQTT broker
    this.mqttSubscriber.connect(this.userId, accessToken);

    dispatch(setConnectionStatus(NotifierConnectionStatus.AUTHENTICATED));
  }

  public emit(event: string, data: any): void {
    // MQTT is subscribe-only for frontend, no emit needed
    console.log(`[Notifier] MQTT frontend is subscribe-only, cannot emit event [${event}]`);
  }

  private extractUserIdFromToken(token: string): string | null {
    try {
      // Decode JWT token to extract user ID
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      return payload.userId || payload.sub || payload.id || null;
    } catch (error) {
      console.error('[Notifier] Error decoding JWT token:', error);
      return null;
    }
  }

  private setupMQTTHandlers(): void {
    // Clear existing handlers first to avoid duplicates
    // Note: this will replace any existing handlers for these event types
    console.log('[Notifier] Setting up MQTT message handlers');

    // Handle sync-user-data events
    this.mqttSubscriber.onMessage("sync-user-data", async (updateEvent: UpdateEvent) => {
      const subscriptionListRes = await dispatch(DBSubscriptionThunks.find({}));
      const subscriptionList: Subscription[] = subscriptionListRes.payload;
      dispatch(validateSubscriptionsThunk(subscriptionList));
    });

    // Handle chatbot progress updates
    this.mqttSubscriber.onMessage("chatbot-progress", (progressEvent) => {
      if (progressEvent && progressEvent.message) {
        dispatch(setLoaderProgressMessage(progressEvent.message));
      }
    });

    // Handle document processing progress
    this.mqttSubscriber.onMessage("document-processing-progress", (progressEvent) => {
      dispatch(updateDocumentProgress(progressEvent));
    });

    // Handle general update events - directly process them without queueing
    this.mqttSubscriber.onMessage("update", async (eventMessage: UpdateEventMessageData) => {
      // Handle whatsapp interface events separately
      if (eventMessage.path === 'whatsapp-interface') {
        if (isExtensionContext()) {
          await handleWhatsappInterfaceEvents(eventMessage, undefined);
        }
        return;
      }

      // Process entity updates directly - no need for message recovery with MQTT
      await processMessage(eventMessage);
    });

    // Handle settings update events
    this.mqttSubscriber.onMessage("settings-update", (settingsData: any) => {
      dispatch(setSettings(settingsData));
    });

    this.mqttSubscriber.onMessage("wa-message-listener-event", async (data) => {

      if (isExtensionContext()) return;
      const { msg, type } = data?.data ?? {};
      // Events can arrive without a message payload (e.g. a removal whose
      // chat has no remaining lastMessage) — nothing to apply, don't throw.
      if (!msg || typeof msg !== 'object') return;
      const state: CommonRootState = getStateDirectly();

      // Retrieve the target chat from the new cache system.
      // Canonical chat JID: the backend derives top-level `chatId` from the
      // message's id.remote (now @c.us after the incoming payload is
      // reconciled at the engine), but fall back to the always-@c.us
      // `waMessageKey.remote` so an @lid-aliased incoming message can never be
      // dropped here (reading a bare `msg.chatId` silently discarded every
      // incoming message in an @lid chat → nothing rendered live).
      const targetChatId = msg.chatId ?? msg?.waMessageKey?.remote;
      if (!targetChatId) return;

      // System notifications — group membership events (joins / leaves /
      // removes / renames = type 'gp2'), the group 'description' change, and
      // encryption/protocol notices — render in the CONVERSATION but must NOT
      // reorder the chat list or replace its preview (WhatsApp treats them as
      // low-priority; a "X joined via an invite link" never bumps a chat up).
      // Shared guard so requestChatByIdThunk's re-fetch path stays in sync.
      const isSystemNotification = isSystemNotificationMessage(msg);

      // Extract the message ID. It can be in different properties depending on the event type.
      const msgId = msg?.id?._serialized; //will be undefined only for receivedunreadcountchanged

      const currentChatId = state.TeamInboxReducer?.chatViewMode?.chatId ?? null;
      const cachedChat = state.TeamInboxReducer?.chatViewMode?.tempChatData ?? null;
      const isChatlistInSearchMode = state.TeamInboxReducer?.filteredChatAndContactList?.searchResponseStatus !== 'noSearchValue';
      // Check if the affected chat is the one currently open in the main view.
      const isCurrentChatOpened = targetChatId === currentChatId;


      // Ignore status updates from WhatsApp, as they are not standard chats.
      if (chatKindOf(targetChatId) === 'status') return;

      // If the chat collection is empty and there is no cached chat, no need to continue
      if (state.DBTeamInboxChatsCacheReducer.ids.length === 0 && !cachedChat) return;

      // Get the target chat from the cache using the chatId
      let targetChat = state.DBTeamInboxChatsCacheReducer.entities[targetChatId];

      if (!targetChat) {
        // if targetChat is not found, it might be: 
        // 1. a new conversation
        // 2. a new message in an old chat
        // 3. a new message in a cached chat that was not found in the local list.
        // If the event is a new message and the chatlist is not in search mode, we fetch the full chat details from the extension.
        if (type === "receivedNewMessage" && !isChatlistInSearchMode) {
          // Pass the triggering message so the fetched chat is upserted WITH
          // its fresh preview under the canonical @c.us key — the updateOne
          // below no-ops when targetChatId is the @lid form of an already
          // @c.us-cached chat (which is what created the duplicate row). For a
          // system notification, DON'T seed the preview/sort from it (else an
          // uncached chat would surface at the top on a join/leave) — let the
          // fetched chat keep its own real last-message preview.
          dispatch(requestChatByIdThunk({
            chatId: targetChatId,
            ...(isSystemNotification ? {} : { lastMessage: msg, timestamp: msg?.timestamp }),
          }));
        }
        if (!isCurrentChatOpened) {
          // if the chat is not opened, we don't need to continue
          return;
        }
      }

      let isThisUpdateForMessageLastInChat = false;
      let targetMessageId: string | undefined;

      // Determine what message id to use
      if (type === "receivedMessageRevoked") {
        targetMessageId = msg?.protocolMessageKey?._serialized;
      } else if (
        type === "receivedCiphertextMessageAdded" ||
        type === "receivedNewMessage" ||
        type === "receivedMessageAckUpdate" ||
        type === "receivedMessageRemoved"
      ) {
        targetMessageId = msgId;
      }

      /**
       * This switch statement routes different real-time WhatsApp events to the appropriate logic.
       * Each case handles a specific event type, dispatching Redux actions to update the UI.
       */
      switch (type) {
        /**
         * Handles new incoming messages.
         */
        case "receivedCiphertextMessageAdded":
        case "receivedNewMessage":

          // Real conversation messages update the preview + sort time (reorder);
          // system notifications (isSystemNotification, computed above) skip the
          // chat-entity update entirely — no preview jump, no reorder — but are
          // still added to the open conversation below.
          if (!isSystemNotification) {
            dispatch(TeamInboxChatsCacheReducerActions.updateOne({
              id: targetChatId,
              changes: {
                lastMessage: msg,          // preview = newest real message
                timestamp: msg.timestamp,  // (getChatTimestamp also reads lastMessage.t)
              },
            }));
          }

          // If the affected chat is currently open, add the new message to the message cache.
          if (isCurrentChatOpened) {

            if (!msg.fromMe) dispatch(requestSendSeenByChatIdThunk(targetChatId));

            const newMessage = {
              ...msg,
              _id: targetMessageId,
              // Store under the canonical @c.us chat JID so
              // selectCurrentChatMessages (which filters by the @c.us
              // currentChatId) actually renders it — a bare/@lid `msg.chatId`
              // would be filtered straight back out.
              chatId: targetChatId,
            };

            // Merge-preserving ingest: when the cache already holds this
            // message (optimistic send insert, or a refetch raced the live
            // event), addOne would silently no-op and updateOne with the raw
            // payload would clobber. Instead merge only the fields the live
            // payload actually carries — enrichment fields the event lacks
            // (quotedMessage / reactions from the query projection) survive
            // because the undefined-filter keeps them out of `changes`.
            const existingMessage = targetMessageId
              ? state.DBTeamInboxMessagesCacheReducer?.entities?.[targetMessageId]
              : undefined;

            // The exact-id lookup misses when the live event serializes the
            // key differently from the entity we already hold (participant
            // suffix present/absent, @lid vs @c.us) — the send-response
            // optimistic insert and the own_message live event are the known
            // offenders, briefly rendering the same message TWICE until a
            // refetch collapses them. The short stanza id (the 3rd `_`
            // segment; fromMe and the chat JID never contain underscores) is
            // serialization-stable, so fall back to a same-chat scan on it.
            const shortIdOf = (serialized?: string | null) =>
              (serialized || '').split('_')[2] || null;
            const incomingShortId =
              msg?.waMessageKey?.id || shortIdOf(targetMessageId);
            const keyVariantMatch = (!existingMessage && incomingShortId)
              ? Object.values(state.DBTeamInboxMessagesCacheReducer?.entities || {}).find(
                  (m: any) =>
                    m?.chatId === targetChatId &&
                    (m?.waMessageKey?.id || shortIdOf(m?._id)) === incomingShortId,
                )
              : undefined;

            if (existingMessage || keyVariantMatch) {
              const mergeTargetId = existingMessage ? targetMessageId : (keyVariantMatch as any)._id;
              dispatch(TeamInboxMessagesCacheReducerActions.updateOne({
                id: mergeTargetId,
                changes: Object.fromEntries(
                  Object.entries(newMessage).filter(([k, v]) =>
                    v !== undefined
                    // Merging into a key-variant entity must not rewrite its
                    // identity fields — the entity id stays as-is.
                    && !(keyVariantMatch && (k === '_id' || k === 'id' || k === 'waMessageKey')),
                  ),
                ),
              }));
            } else {
              dispatch(TeamInboxMessagesCacheReducerActions.addOne(newMessage));
            }
          }
          break;
        /**
         * Handles updates to message acknowledgments (e.g., sent, delivered, read).
         */
        case "receivedMessageAckUpdate":

          // Check if the message that received an acknowledgment update is the last message in the chat preview.
          isThisUpdateForMessageLastInChat = lastMessageKeyOf(targetChat) === msgId;

          if (isThisUpdateForMessageLastInChat) {
            // If it's the last message, update its ack status in the chat list preview using cache
            dispatch(TeamInboxChatsCacheReducerActions.updateOne({
              id: targetChatId,
              changes: {
                lastMessage: {
                  ...targetChat.lastMessage,
                  ack: msg.ack
                }
              }
            }));
          }

          // If the affected chat is currently open, update the ack of the specific message in the message cache.
          if (!isCurrentChatOpened) break;
          if (!targetMessageId) break;

          dispatch(TeamInboxMessagesCacheReducerActions.updateOne({
            id: targetMessageId,
            changes: {
              ack: msg.ack
            }
          }));
          break;
        /**
         * Handles the removal of a message, such as when a user selects "delete for me".
         */
        case "receivedMessageRemoved":

          // Check if the removed message was the last one in the chat preview.
          isThisUpdateForMessageLastInChat = lastMessageKeyOf(targetChat) === targetMessageId;

          // The event provides the new last message for the chat — it can be
          // null when the removed message was the only one left.
          if (isThisUpdateForMessageLastInChat && msg.lastMessage) {
            const newLastMessage = msg.lastMessage;

            // Update the chat preview with the new last message using cache
            dispatch(TeamInboxChatsCacheReducerActions.updateOne({
              id: targetChatId,
              changes: {
                lastMessage: newLastMessage,
                timestamp: newLastMessage.timestamp
              }
            }));
          }

          // If the chat is open, find the removed message in the view and mark it as deleted.
          if (isCurrentChatOpened && targetMessageId) {
            dispatch(TeamInboxMessagesCacheReducerActions.updateOne({
              id: targetMessageId,
              changes: { deleted: true }
            }));
          }
          break;
        /**
         * Handles messages that were revoked by the sender ("delete for everyone").
         */
        case "receivedMessageRevoked":

          // Check if the revoked message was the last one in the chat preview.
          isThisUpdateForMessageLastInChat = lastMessageKeyOf(targetChat) === msg.protocolMessageKey?._serialized;

          if (isThisUpdateForMessageLastInChat) {
            // The `msg` object now represents the revocation status.
            // We update the last message in the chat preview to show this status using cache
            dispatch(TeamInboxChatsCacheReducerActions.updateOne({
              id: targetChatId,
              changes: {
                lastMessage: msg
              }
            }));
          }

          if (!isCurrentChatOpened) return;
          if (!targetMessageId) break;

          dispatch(TeamInboxMessagesCacheReducerActions.updateOne({
            id: targetMessageId,
            changes: {
              ...msg,
              _id: msg.id._serialized  //assign the new id to the message
            }
          }));

          break;
        /**
         * Handles changes in the unread message count for a chat.
         */
        case "receivedUnreadCountChanged":

          // Update the chat with the new unread count using cache
          dispatch(TeamInboxChatsCacheReducerActions.updateOne({
            id: targetChatId,
            changes: {
              unreadCount: msg.unreadCount,
              timestamp: msg.timestamp
            }
          }));

          break;
        default:
          // Unknown event types are silently ignored
      }
    });
  }
} 